import React, { useEffect, useState } from "react";
import { Plus, Trash2, Save, GripVertical } from "lucide-react";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

const newRow = (order = 0) => ({
  id: crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2),
  label_it: "",
  label_en: "",
  label_de: "",
  time: "",
  order,
});

const HoursManager = ({ authedAxios }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const res = await authedAxios().get("/settings/hours");
        setRows(res.data?.rows || []);
      } catch {
        toast.error("Errore caricamento orari");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [authedAxios]);

  const updateRow = (idx, key, value) =>
    setRows((s) => s.map((r, i) => (i === idx ? { ...r, [key]: value } : r)));

  const removeRow = (idx) =>
    setRows((s) => s.filter((_, i) => i !== idx).map((r, i) => ({ ...r, order: i + 1 })));

  const addRow = () => setRows((s) => [...s, newRow(s.length + 1)]);

  const moveUp = (idx) => {
    if (idx === 0) return;
    setRows((s) => {
      const next = [...s];
      [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
      return next.map((r, i) => ({ ...r, order: i + 1 }));
    });
  };

  const moveDown = (idx) => {
    setRows((s) => {
      if (idx >= s.length - 1) return s;
      const next = [...s];
      [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
      return next.map((r, i) => ({ ...r, order: i + 1 }));
    });
  };

  const save = async () => {
    for (const r of rows) {
      if (!r.label_it.trim() || !r.label_en.trim() || !r.label_de.trim() || !r.time.trim()) {
        toast.error("Compila tutti i campi di ogni riga");
        return;
      }
    }
    setSaving(true);
    try {
      const payload = {
        rows: rows.map((r, i) => ({
          id: r.id,
          label_it: r.label_it.trim(),
          label_en: r.label_en.trim(),
          label_de: r.label_de.trim(),
          time: r.time.trim(),
          order: i + 1,
        })),
      };
      const res = await authedAxios().put("/settings/hours", payload);
      setRows(res.data?.rows || []);
      toast.success("Orari aggiornati");
    } catch (e) {
      toast.error("Errore salvataggio orari");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section data-testid="admin-hours-section" className="mb-14">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div>
          <p className="overline text-[color:var(--wine)] flex items-center">
            <span className="section-line"></span>
            Orari di apertura
          </p>
          <h2 className="mt-4 font-serif-display text-3xl">Giorni & orari</h2>
        </div>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={addRow}
            data-testid="hours-add-row"
            className="btn-ghost"
            style={{ padding: "0.6rem 1rem" }}
          >
            <Plus size={14} strokeWidth={1.6} />
            Aggiungi riga
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving || loading}
            data-testid="hours-save"
            className="btn-primary disabled:opacity-50"
            style={{ padding: "0.6rem 1.2rem" }}
          >
            <Save size={14} strokeWidth={1.6} />
            {saving ? "Salvataggio..." : "Salva orari"}
          </button>
        </div>
      </div>

      {loading ? (
        <p className="text-[color:var(--ink-3)]">Caricamento...</p>
      ) : rows.length === 0 ? (
        <p className="text-[color:var(--ink-3)] italic">Nessuna riga. Clicca su &quot;Aggiungi riga&quot;.</p>
      ) : (
        <div className="space-y-3">
          {rows.map((r, idx) => (
            <div
              key={r.id}
              data-testid={`hours-row-${idx}`}
              className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end bg-[color:var(--ivory-2)] p-4 border border-[color:var(--line)]"
            >
              <div className="md:col-span-1 flex items-center gap-1 text-[color:var(--ink-3)]">
                <button type="button" onClick={() => moveUp(idx)} title="Su" className="text-xs hover:text-[color:var(--wine)]">▲</button>
                <button type="button" onClick={() => moveDown(idx)} title="Giù" className="text-xs hover:text-[color:var(--wine)]">▼</button>
                <GripVertical size={14} strokeWidth={1.2} />
              </div>
              <div className="md:col-span-3">
                <Label className="overline text-[color:var(--ink-3)]">Etichetta IT</Label>
                <Input
                  value={r.label_it}
                  onChange={(e) => updateRow(idx, "label_it", e.target.value)}
                  data-testid={`hours-label-it-${idx}`}
                  placeholder="Lun – Ven"
                  className="mt-1 rounded-none border-0 border-b border-[color:var(--line)] bg-transparent focus-visible:ring-0 focus-visible:border-[color:var(--wine)] px-0"
                />
              </div>
              <div className="md:col-span-3">
                <Label className="overline text-[color:var(--ink-3)]">Etichetta EN</Label>
                <Input
                  value={r.label_en}
                  onChange={(e) => updateRow(idx, "label_en", e.target.value)}
                  data-testid={`hours-label-en-${idx}`}
                  placeholder="Mon – Fri"
                  className="mt-1 rounded-none border-0 border-b border-[color:var(--line)] bg-transparent focus-visible:ring-0 focus-visible:border-[color:var(--wine)] px-0"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="overline text-[color:var(--ink-3)]">Etichetta DE</Label>
                <Input
                  value={r.label_de}
                  onChange={(e) => updateRow(idx, "label_de", e.target.value)}
                  data-testid={`hours-label-de-${idx}`}
                  placeholder="Mo – Fr"
                  className="mt-1 rounded-none border-0 border-b border-[color:var(--line)] bg-transparent focus-visible:ring-0 focus-visible:border-[color:var(--wine)] px-0"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="overline text-[color:var(--ink-3)]">Orario</Label>
                <Input
                  value={r.time}
                  onChange={(e) => updateRow(idx, "time", e.target.value)}
                  data-testid={`hours-time-${idx}`}
                  placeholder="08:30 – 18:00"
                  className="mt-1 rounded-none border-0 border-b border-[color:var(--line)] bg-transparent focus-visible:ring-0 focus-visible:border-[color:var(--wine)] px-0"
                />
              </div>
              <div className="md:col-span-1 flex justify-end">
                <button
                  type="button"
                  onClick={() => removeRow(idx)}
                  data-testid={`hours-remove-${idx}`}
                  className="p-2 text-[color:var(--ink-3)] hover:text-[color:var(--wine)]"
                  title="Rimuovi"
                >
                  <Trash2 size={16} strokeWidth={1.4} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      <p className="mt-4 text-xs text-[color:var(--ink-3)] italic">
        Suggerimento: per indicare un giorno di chiusura inserisci &quot;Chiuso&quot; / &quot;Closed&quot; / &quot;Geschlossen&quot; nel campo Orario.
      </p>
    </section>
  );
};

export default HoursManager;
